// public/js/features/assets/slot-utils.js
// Client mirror of the server slot label logic (utils/slots.mjs). A "slot" is
// the logical seat a device occupies in an asset; its label drives display in
// the map info card, sensor charts and the paired-devices section. Falls back
// to a unique "<Role> <shortId>" — never subType (identical sensors collide).

const ROLE_NOUN = { tracker: 'Tracker', sensor: 'Sensor', beacon: 'Beacon', unknown: 'Device' };
const BLE_BEACON_SUBTYPES = new Set(['iBeacon', 'Eddystone', 'Eddystone-TLM']);

export function deviceRole(d) {
  if (!d) return 'unknown';
  if (d.type === 'bluetooth') return BLE_BEACON_SUBTYPES.has(d.subType) ? 'beacon' : 'sensor';
  if (d.type === 'digitalmatter') return 'tracker';
  return d.type || 'unknown';
}

function shortId(device, deviceId) {
  const hex = String(device?.mac || '').replace(/[^0-9a-fA-F]/g, '');
  if (hex.length >= 4) return hex.slice(-4).toUpperCase();
  return String(deviceId || device?.deviceId || '').slice(-4).toUpperCase() || 'DEV';
}

// The slot record (from asset.deviceSlots) currently holding a device.
export function slotForDevice(asset, deviceId) {
  return (asset?.deviceSlots || []).find(s => s.deviceId === deviceId) || null;
}

export function slotDisplayLabel(slot, device) {
  if (slot?.label && slot.label.trim()) return slot.label.trim();
  const role = slot?.role || deviceRole(device);
  return `${ROLE_NOUN[role] || 'Device'} ${shortId(device, slot?.deviceId)}`;
}

// Convenience: resolve a device's display label straight from the asset.
export function labelForDevice(asset, device) {
  return slotDisplayLabel(slotForDevice(asset, device?.deviceId), device);
}

// "<slot label> <metric>", e.g. "Engine Bag Temperature".
export function slotMetricLabel(asset, device, metricLabel) {
  const base = labelForDevice(asset, device);
  return metricLabel ? `${base} ${metricLabel}` : base;
}

// { deviceId → slot display label } for an asset — passed to the chart grid so
// per-device series carry their slot name. Covers currently-paired devices.
export function deviceLabelMap(asset) {
  const out = {};
  const devs = asset?.assignedDevices || [];
  for (const slot of (asset?.deviceSlots || [])) {
    if (!slot.deviceId) continue;
    const dev = devs.find(d => d.deviceId === slot.deviceId);
    out[slot.deviceId] = slotDisplayLabel(slot, dev);
  }
  return out;
}
